import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    bulkResolveSos,
    getSosBulkContext,
    SosBulkContextRow,
    SosResolution,
} from "../../../api/RosterApi";
import { getLkupByName } from "../../../api/LookupApi";
import { PersonnelTypes } from "../../../constants/PersonnelTypes";
import { useAlert } from "../../../contexts/AlertsContext";
import { useGlobalModal } from "../../../contexts/GlobalModalContext";
import { createLookup, Lookup } from "../../main/ts/Utils";
import LoadingGif from "../LoadingGif";
import { OutlinedButtonStyled } from "../OutlinedButtonStyled";
import { PrimaryButtonStyled } from "../PrimaryButtonStyled";
import BulkReviewResolveAccordionHeader from "./bulkReviewResolve/BulkReviewResolveAccordionHeader";
import BulkReviewResolveCollapseToolbar from "./bulkReviewResolve/BulkReviewResolveCollapseToolbar";
import {
    AccordionBody,
    AccordionItem,
    ActionRow,
    ActionSection,
    CommentBlock,
    CommentBlockLabel,
    CommentSection,
    CommentText,
    ErrorText,
    LoadingContainer,
    ModalBody,
    MoveToLabel,
    StyledSelect,
} from "./bulkReviewResolve/bulkReviewResolveStyles";
import {
    buildSosCommentDisplayBlocks,
    formatMoveToChoiceLabel,
} from "./bulkReviewResolve/bulkReviewResolveUtils";

export type BulkReviewResolveSosRowState = SosBulkContextRow & {
    targetStatus: string;
};

export const isBulkReviewResolveSosRowComplete = (row: BulkReviewResolveSosRowState): boolean =>
    Boolean(row.targetStatus);

type BulkReviewResolveSosModalBodyProps = {
    personnelIds: number[];
    rows: BulkReviewResolveSosRowState[];
    setRows: React.Dispatch<React.SetStateAction<BulkReviewResolveSosRowState[]>>;
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    error: string | null;
    setError: React.Dispatch<React.SetStateAction<string | null>>;
};

const BulkReviewResolveSosModalBody: React.FC<BulkReviewResolveSosModalBodyProps> = ({
    personnelIds,
    rows,
    setRows,
    loading,
    setLoading,
    error,
    setError,
}) => {
    const { closeModal } = useGlobalModal();
    const showAlert = useAlert();
    const [expandedIds, setExpandedIds] = useState<number[]>([]);
    const [statusOptions, setStatusOptions] = useState<Lookup[]>([]);

    useEffect(() => {
        if (personnelIds.length === 0) {
            setLoading(false);
            setRows([]);
            return;
        }

        setLoading(true);
        setError(null);

        Promise.all([getSosBulkContext(personnelIds), getLkupByName("moveto")])
            .then(([contextResponse, movetoResponse]) => {
                const orderMap = new Map(personnelIds.map((id, index) => [id, index]));
                const sorted = [...(contextResponse.rows || [])].sort(
                    (a, b) =>
                        (orderMap.get(a.personnelId) ?? 0) - (orderMap.get(b.personnelId) ?? 0),
                );
                setRows(
                    sorted.map(row => ({
                        ...row,
                        targetStatus: "",
                    })),
                );

                if (movetoResponse && Array.isArray(movetoResponse)) {
                    const mapped: Lookup[] = movetoResponse.map((item: any, index: number) => {
                        const value: string =
                            item.value ?? item.status ?? item.label ?? String(item);
                        const id: string | number = item.id ?? index;
                        const activeFlag: boolean =
                            item.activeFlag !== undefined ? item.activeFlag : true;
                        return createLookup(id, value, activeFlag);
                    });
                    const withLabels = mapped.map((lk, i) => ({
                        ...lk,
                        label:
                            (movetoResponse[i] &&
                                (movetoResponse[i].label ?? movetoResponse[i].status)) ||
                            lk.label,
                    }));
                    setStatusOptions(withLabels);
                }

                setLoading(false);
            })
            .catch(() => {
                setError("Failed to load SOS context. Please try again.");
                setLoading(false);
                showAlert(
                    "Error loading SOS resolve context. Please contact BIGS Systems.",
                    "danger",
                );
            });
    }, [setLoading]);

    const toggleExpanded = useCallback((personnelId: number) => {
        setExpandedIds(prev =>
            prev.includes(personnelId)
                ? prev.filter(id => id !== personnelId)
                : [...prev, personnelId],
        );
    }, []);

    const removeRow = useCallback(
        (personnelId: number, e: React.MouseEvent) => {
            e.stopPropagation();
            setRows(prev => {
                const next = prev.filter(r => r.personnelId !== personnelId);
                if (next.length === 0) {
                    closeModal();
                }
                return next;
            });
            setExpandedIds(prev => prev.filter(id => id !== personnelId));
        },
        [closeModal, setRows],
    );

    const updateRow = useCallback(
        (personnelId: number, patch: Partial<BulkReviewResolveSosRowState>) => {
            setRows(prev =>
                prev.map(row => (row.personnelId === personnelId ? { ...row, ...patch } : row)),
            );
        },
        [setRows],
    );

    const collapseRow = useCallback((personnelId: number) => {
        setExpandedIds(prev => prev.filter(id => id !== personnelId));
    }, []);

    if (loading) {
        return (
            <ModalBody>
                <LoadingContainer>
                    <LoadingGif />
                </LoadingContainer>
            </ModalBody>
        );
    }

    if (error) {
        return (
            <ModalBody>
                <ErrorText>{error}</ErrorText>
            </ModalBody>
        );
    }

    return (
        <ModalBody>
            <BulkReviewResolveCollapseToolbar
                expandedCount={expandedIds.length}
                onCollapseAll={() => setExpandedIds([])}
            />
            {rows.map(row => {
                const expanded = expandedIds.includes(row.personnelId);
                const complete = isBulkReviewResolveSosRowComplete(row);
                const choiceLabel = formatMoveToChoiceLabel(row.targetStatus);
                const commentBlocks = buildSosCommentDisplayBlocks(
                    row.sosComment,
                    row.sentToSosComment,
                );

                return (
                    <AccordionItem key={row.personnelId}>
                        <BulkReviewResolveAccordionHeader
                            personnelId={row.personnelId}
                            firstName={row.firstName}
                            middleName={row.middleName}
                            lastName={row.lastName}
                            affiliationAbbrev={row.affiliationAbbrev}
                            complete={complete}
                            choiceLabel={choiceLabel}
                            expanded={expanded}
                            onToggle={() => toggleExpanded(row.personnelId)}
                            onRemove={e => removeRow(row.personnelId, e)}
                        />
                        {expanded ? (
                            <AccordionBody>
                                <CommentSection>
                                    {commentBlocks.length > 0 ? (
                                        commentBlocks.map(block => (
                                            <CommentBlock key={block.label}>
                                                <CommentBlockLabel>{block.label}</CommentBlockLabel>
                                                <CommentText>{block.text}</CommentText>
                                            </CommentBlock>
                                        ))
                                    ) : (
                                        <CommentText>No comment recorded.</CommentText>
                                    )}
                                </CommentSection>
                                <ActionSection>
                                    <ActionRow>
                                        <MoveToLabel>Move to</MoveToLabel>
                                        <StyledSelect
                                            aria-label="Move to"
                                            title="Move to"
                                            value={row.targetStatus}
                                            onChange={e => {
                                                const targetStatus = e.target.value;
                                                updateRow(row.personnelId, { targetStatus });
                                                if (targetStatus) {
                                                    collapseRow(row.personnelId);
                                                }
                                            }}
                                        >
                                            <option value="">Select status...</option>
                                            {statusOptions.map(option => (
                                                <option key={option.id} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </StyledSelect>
                                    </ActionRow>
                                </ActionSection>
                            </AccordionBody>
                        ) : null}
                    </AccordionItem>
                );
            })}
        </ModalBody>
    );
};

type BulkReviewResolveSosModalFooterProps = {
    rows: BulkReviewResolveSosRowState[];
    loading: boolean;
    personnelAdvancedSearch(arg1: any, arg2: any, arg3: any, arg4: any): void;
    adminSelectedMenuOptions: Array<any>;
    adminAffiliationFilters: Array<any>;
    adminSearchText: Array<any> | string;
};

const BulkReviewResolveSosModalFooter: React.FC<BulkReviewResolveSosModalFooterProps> = ({
    rows,
    loading,
    personnelAdvancedSearch,
    adminSelectedMenuOptions,
    adminAffiliationFilters,
    adminSearchText,
}) => {
    const { closeModal, dispatch } = useGlobalModal();
    const showAlert = useAlert();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const allRowsComplete = useMemo(
        () => rows.length > 0 && rows.every(isBulkReviewResolveSosRowComplete),
        [rows],
    );

    const refreshSearch = () => {
        setTimeout(
            () =>
                personnelAdvancedSearch(
                    adminSearchText && adminSearchText.length !== 0 ? adminSearchText : null,
                    adminAffiliationFilters ? adminAffiliationFilters.map(opt => opt.orgId) : null,
                    adminSelectedMenuOptions.map(opt => opt.id),
                    PersonnelTypes.CLUB,
                ),
            1000,
        );
    };

    const handleResolve = async () => {
        setIsSubmitting(true);
        try {
            const resolutions: SosResolution[] = rows.map(row => ({
                personnelId: row.personnelId,
                targetStatus: row.targetStatus,
            }));

            const result = await bulkResolveSos(resolutions);
            closeModal();
            dispatch({
                type: "openBulkReviewResolveResultsModal",
                bulkReviewResolveResults: result,
                personnelAdvancedSearch,
                adminSelectedMenuOptions,
                adminAffiliationFilters,
                adminSearchText,
            });
            refreshSearch();
        } catch {
            showAlert("Failed to resolve workflows. Please try again.", "danger");
            setIsSubmitting(false);
        }
    };

    return (
        <div className="d-flex justify-content-end w-100">
            <div className="mr-2">
                <PrimaryButtonStyled
                    width={100}
                    height={26}
                    disabled={isSubmitting || loading || !allRowsComplete}
                    onClick={event => {
                        event.stopPropagation();
                        handleResolve();
                    }}
                >
                    {isSubmitting ? "Resolving..." : "Resolve"}
                </PrimaryButtonStyled>
            </div>
            <OutlinedButtonStyled
                width={80}
                height={26}
                disabled={isSubmitting}
                onClick={event => {
                    event.stopPropagation();
                    closeModal();
                }}
            >
                Cancel
            </OutlinedButtonStyled>
        </div>
    );
};

BulkReviewResolveSosModalBody.displayName = "BodyContent";
BulkReviewResolveSosModalFooter.displayName = "FooterContent";

export { BulkReviewResolveSosModalBody, BulkReviewResolveSosModalFooter };
